import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const STORAGE_KEY = 'atharva.cart.v1';

const emptyCart = () => ({
  organisation_id: null,
  organisation_name: '',
  items: [],
});

function load() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return emptyCart();
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.items)) return emptyCart();
    return parsed;
  } catch {
    return emptyCart();
  }
}

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const [cart, setCart] = useState(load);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cart));
  }, [cart]);

  const addItem = useCallback((listing, quantity) => {
    setCart((prev) => {
      const item = {
        listing_id: listing.id,
        listing_title: listing.title,
        crop_name: listing.crop_name,
        organisation_id: listing.organisation,
        organisation_name: listing.organisation_name,
        unit: listing.unit,
        price_per_unit: Number(listing.price_per_unit),
        quantity: Number(quantity),
      };
      // Enforce one-FPO-per-cart.
      if (prev.items.length && prev.organisation_id !== listing.organisation) {
        const ok = window.confirm(
          `Your cart already has items from "${prev.organisation_name}". Replace with items from "${listing.organisation_name}"?`,
        );
        if (!ok) return prev;
        return {
          organisation_id: listing.organisation,
          organisation_name: listing.organisation_name,
          items: [item],
        };
      }
      // Merge with existing line if same listing already in cart.
      const existing = prev.items.findIndex((i) => i.listing_id === listing.id);
      const items = [...prev.items];
      if (existing >= 0) {
        items[existing] = { ...items[existing], quantity: items[existing].quantity + Number(quantity) };
      } else {
        items.push(item);
      }
      return {
        organisation_id: listing.organisation,
        organisation_name: listing.organisation_name,
        items,
      };
    });
  }, []);

  const updateQuantity = useCallback((listing_id, quantity) => {
    setCart((prev) => ({
      ...prev,
      items: prev.items.map((i) =>
        i.listing_id === listing_id ? { ...i, quantity: Number(quantity) } : i,
      ),
    }));
  }, []);

  const removeItem = useCallback((listing_id) => {
    setCart((prev) => {
      const items = prev.items.filter((i) => i.listing_id !== listing_id);
      if (items.length === 0) return emptyCart();
      return { ...prev, items };
    });
  }, []);

  const clear = useCallback(() => setCart(emptyCart()), []);

  const totals = useMemo(() => {
    const count = cart.items.reduce((acc, i) => acc + i.quantity, 0);
    const subtotal = cart.items.reduce((acc, i) => acc + i.quantity * i.price_per_unit, 0);
    return { count, subtotal, itemCount: cart.items.length };
  }, [cart]);

  const value = useMemo(
    () => ({ cart, addItem, updateQuantity, removeItem, clear, totals }),
    [cart, addItem, updateQuantity, removeItem, clear, totals],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used inside <CartProvider>');
  return ctx;
}
